import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { ajax } from "@rails/ujs"

export default class extends Controller {
  static targets = ["column"]

  connect() {
    this.sortables = this.columnTargets.map((column) => new Sortable(column, {
      // The available list is a palette: dragging from it adds a copy, so elements can repeat
      group: { name: "shared", pull: this.isAvailable(column) ? "clone" : true },
      sort: !this.isAvailable(column),
      filter: ".no-drag",
      animation: 150,
      forceFallback: true,
      scrollSensitivity: 20,
      scrollSpeed: 20,
      revertOnSpill: true,
      emptyInsertThreshold: 20,
      ghostClass: "list-group-item-primary",
      onEnd: this.moved.bind(this)
    }))
  }

  disconnect() {
    this.sortables.forEach((sortable) => sortable.destroy())
    this.sortables = []
  }

  isAvailable(column) {
    return column.dataset.column === undefined
  }

  moved(event) {
    const item = event.item
    const column = event.to.dataset.column
    if (column === undefined && !item.dataset.id) return

    // The available list already shows every element, so a removed one is not kept there
    if (column === undefined) return this.destroy(item)

    this.showControls(item)
    const position = event.newIndex
    if (item.dataset.id) this.request(this.elementUrl(item), "PATCH", { column, position })
    else this.create(item, column, position)
  }

  showControls(item) {
    item.querySelector(".close").hidden = false
    const template = item.querySelector("template")
    if (template) template.replaceWith(template.content.cloneNode(true))
  }

  // Narrows the available list to one category, blank showing all
  showCategory(event) {
    const category = event.currentTarget.value
    const available = this.columnTargets.find((column) => this.isAvailable(column))
    available.querySelectorAll(":scope > [data-name]").forEach((item) => {
      item.classList.toggle("d-none", category !== "" && item.dataset.category !== category)
    })
  }

  toggleContacts(event) {
    const options = event.currentTarget.parentElement.querySelector("[data-contact-options]")
    options.hidden = !options.hidden
  }

  // No contacts checked means the element shows all of its filter's contacts
  filter(event) {
    const item = event.target.closest("[data-name]")
    const checked = Array.from(item.querySelectorAll("[data-contact-options] input:checked"))
    const names = checked.map((input) => input.parentElement.textContent.trim())
    const summary = item.querySelector("[data-contact-summary]")
    summary.textContent = names.length ? new Intl.ListFormat("en").format(names) : summary.dataset.allLabel
    this.saveContacts(item)
  }

  // Waits for the id of a just added element; create sends the filter once it arrives
  saveContacts(item) {
    if (!item.dataset.id) return

    const checked = Array.from(item.querySelectorAll("[data-contact-options] input:checked"))
    const contactIds = checked.length ? checked.map((input) => input.value) : [""]
    const data = contactIds.map((contactId) => ["contact_ids[]", contactId])
    this.request(`${this.elementUrl(item)}/contacts`, "PATCH", data)
  }

  remove(event) {
    this.destroy(event.currentTarget.closest("[data-name]"))
  }

  create(item, column, position) {
    const data = { element: item.dataset.name, column: column, position: position }
    this.request("/admin/dashboard/elements", "POST", data, (response) => {
      item.dataset.id = response.id
      if (item.querySelector("[data-contact-options] input:checked")) this.saveContacts(item)
    })
  }

  destroy(item) {
    item.remove()
    if (item.dataset.id) this.request(this.elementUrl(item), "DELETE", {})
  }

  elementUrl(item) {
    return `/admin/dashboard/elements/${item.dataset.id}`
  }

  // data is anything URLSearchParams accepts; failures respond with the standard errors toast
  request(url, type, data, success) {
    ajax({
      url: url,
      type: type,
      dataType: "script",
      data: new URLSearchParams(data).toString(),
      success: success
    })
  }
}
